import {
  Box,
  Button,
  CustomModalLayout,
  RadioGroup,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent, useState } from 'react';
import {
  ArticleTypesCount,
  getSearchResultsFilterItems,
  mapArticleTypeToEnum,
} from '..';
import { DATA_HOOKS } from '../../../dataHooks';
import { COLORS } from '../../../utils/theme';
import css from './index.module.scss';
import { ArticleType } from '@wix/answers-api';
import { SearchResultsFilter } from '../../../constants';

export type FilterModalProps = {
  activeFilter: SearchResultsFilter;
  articlesTypesCount: ArticleTypesCount;
  locale?: string;
  onFilterChange: (filter: SearchResultsFilter) => void;
  onClose: () => void;
};

export const FilterModal: FunctionComponent<FilterModalProps> = ({
  activeFilter,
  articlesTypesCount,
  locale,
  onFilterChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState(activeFilter);

  const onApply = () => {
    if (selectedFilter !== activeFilter) {
      onFilterChange(selectedFilter);
    }
    onClose();
  };

  const onSelectFilter = (filter: SearchResultsFilter) => {
    setSelectedFilter(filter);
  };

  const allItemsCount =
    articlesTypesCount[ArticleType.ARTICLE] +
    articlesTypesCount[ArticleType.KNOWN_ISSUE] +
    articlesTypesCount[ArticleType.FEATURE_REQUEST];

  return (
    <CustomModalLayout
      dataHook={DATA_HOOKS.FILTER_AND_SORT_MODAL_BODY}
      className={css.filterAndSortModalBody}
      onCloseButtonClick={onClose}
      height="auto"
      title={
        <Text size="medium" className={css.filterHeader}>
          {t('search-results.filter-modal.header')}
        </Text>
      }
      showHeaderDivider={false}
      showFooterDivider={false}
      content={
        <Box direction="vertical">
          <Box
            dataHook={DATA_HOOKS.SEARCH_RESULTS}
            direction="vertical"
            className={css.filterMenu}
            color={COLORS.D10}
          >
            <Text size="small" className={css.articleType} color={COLORS.D10}>
              {t('search-results.filter-option.prefix')}
            </Text>
            <RadioGroup
              dataHook={DATA_HOOKS.SEARCH_RESULTS_MODAL_FILTERS}
              spacing='none'
              selectionAreaPadding='6px'
              value={selectedFilter}
              onChange={(val) => onSelectFilter(val as SearchResultsFilter)}
            >
              {getSearchResultsFilterItems(locale).map((filter) => (
                <RadioGroup.Radio key={filter} value={filter}>
                  {t(`search-results.filter-option.${filter}`, {
                    count:
                      filter === SearchResultsFilter.ALL
                        ? allItemsCount
                        : articlesTypesCount[mapArticleTypeToEnum[filter]],
                  })}
                </RadioGroup.Radio>
              ))}
            </RadioGroup>
            <Button
              size="medium"
              fullWidth={false}
              skin="standard"
              priority="primary"
              className={css.applyButton}
              onClick={onApply}
              dataHook={DATA_HOOKS.SEARCH_RESULTS_MODAL_APPLY_FILTER}
            >
              {t('search-results.filter-modal.apply')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
